import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Button from '../ui/Button';
import Modal from '../ui/Modal';
import FieldAnswerList from '../fields/FieldAnswerList';
import RequiredDocumentsPanel from './RequiredDocumentsPanel';
import { IconCheckCircle } from '../ui/icons';
import { processCandidateStep } from '../../api/candidates';
import { listDocumentTypes } from '../../api/workflow';
import { usePermission } from '../../auth/usePermission';
import { pushToast } from '../ui/toastBus';
import { formatValidationErrors } from '../../utils/errors';

const OPEN_STATUSES = ['pending', 'in_progress'];
const ACTIVE_CANDIDATE_STATUSES = ['registered', 'in_progress'];

// Registration is hidden from the step timeline, so a candidate that was converted from a lead
// (which never answers the registration add-on fields and arrives with unverified documents)
// has no other way to finish it. This banner lists what is still missing and opens a popup to
// fill in the details / documents and complete the registration.
export default function RegistrationCompleteBanner({ candidate }) {
  const [open, setOpen] = useState(false);
  const { can } = usePermission();
  const typesQuery = useQuery({ queryKey: ['document-types'], queryFn: listDocumentTypes });

  const step = (candidate.steps || []).find((s) => s.code === 'registration');
  if (!step || !OPEN_STATUSES.includes(step.status) || !ACTIVE_CANDIDATE_STATUSES.includes(candidate.status)) return null;
  // Redacted payload (no step.view.registration): nothing meaningful to show.
  if (step.missing_view_permission) return null;

  const nameFor = (code) => typesQuery.data?.find((t) => t.code === code)?.name || code;
  const missingDocuments = step.missing_documents || [];
  const missingFields = step.missing_fields || [];
  const canProcess = can(`step.process.${step.code}`);

  return (
    <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-900">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-semibold">Registration is not complete</p>
          {missingFields.length === 0 && missingDocuments.length === 0 ? (
            <p className="mt-1">Everything required is in place — complete the registration to move this candidate forward.</p>
          ) : (
            <div className="mt-1 space-y-1">
              {missingFields.length > 0 && (
                <p>
                  <span className="font-medium">Missing details:</span> {missingFields.map((f) => f.label).join(', ')}
                </p>
              )}
              {missingDocuments.length > 0 && (
                <p>
                  <span className="font-medium">Missing / unverified documents:</span>{' '}
                  {missingDocuments.map(nameFor).join(', ')}
                </p>
              )}
            </div>
          )}
        </div>
        {canProcess && (
          <Button onClick={() => setOpen(true)}>
            <IconCheckCircle className="h-4 w-4" />
            Complete
          </Button>
        )}
      </div>

      {open && <CompleteRegistrationModal candidate={candidate} step={step} onClose={() => setOpen(false)} />}
    </div>
  );
}

function CompleteRegistrationModal({ candidate, step, onClose }) {
  const queryClient = useQueryClient();
  const [answers, setAnswers] = useState(step.answers || {});
  const fields = step.fields || [];
  const missingDocuments = step.missing_documents || [];

  // Autosave: typed answers are persisted shortly after the last keystroke (documents are
  // already saved the moment they're uploaded). The step stays open until "Update & complete".
  const [saveState, setSaveState] = useState('idle');
  const lastSaved = useRef(JSON.stringify(step.answers || {}));
  const latest = useRef(answers);
  latest.current = answers;

  const draftMutation = useMutation({
    mutationFn: (payload) => processCandidateStep(candidate.id, step.candidate_step_id, { action: 'save_draft', answers: payload }),
    // Defined here (not on mutate()) so the banner still refreshes if the popup closed mid-save.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['candidates', String(candidate.id)] }),
  });

  const saveDraft = () => {
    const payload = latest.current;
    const serialized = JSON.stringify(payload);
    if (serialized === lastSaved.current) return;
    lastSaved.current = serialized;
    setSaveState('saving');
    draftMutation.mutate(payload, {
      onSuccess: () => setSaveState('saved'),
      onError: () => {
        lastSaved.current = null;
        setSaveState('error');
      },
    });
  };

  useEffect(() => {
    const t = setTimeout(saveDraft, 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers]);

  // Flush anything typed in the last moments when the popup is closed.
  const closeAndFlush = () => {
    saveDraft();
    onClose();
  };

  const mutation = useMutation({
    mutationFn: () => processCandidateStep(candidate.id, step.candidate_step_id, { action: 'approve', answers }),
    onSuccess: (data) => {
      pushToast({
        type: 'success',
        message: data?.submitted_for_approval ? 'Submitted for approval.' : 'Registration completed.',
      });
      queryClient.invalidateQueries({ queryKey: ['candidates', String(candidate.id)] });
      onClose();
    },
    onError: (err) => {
      const errors = err.response?.data?.errors;
      const message = (errors && formatValidationErrors(errors)) || err.response?.data?.message || 'Could not complete registration.';
      pushToast({ type: 'error', message });
    },
  });

  return (
    <Modal
      open
      onClose={closeAndFlush}
      title="Complete registration"
      size="xl"
      footer={
        <>
          <Button variant="secondary" onClick={closeAndFlush}>
            Close
          </Button>
          <span className="self-center text-xs text-gray-500">
            {saveState === 'saving' && 'Saving…'}
            {saveState === 'saved' && 'All changes saved'}
            {saveState === 'error' && <span className="text-red-600">Could not save — will retry on next change</span>}
          </span>
          <Button className="ml-auto" onClick={() => mutation.mutate()} loading={mutation.isPending}>
            <IconCheckCircle className="h-4 w-4" />
            Update &amp; complete
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {fields.length > 0 && (
          <div>
            <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Details</h4>
            <FieldAnswerList
              fields={fields}
              answers={answers}
              onAnswer={(fieldId, value) => setAnswers((prev) => ({ ...prev, [fieldId]: value }))}
              className="grid grid-cols-1 gap-x-4 gap-y-3 md:grid-cols-2"
            />
          </div>
        )}

        <RequiredDocumentsPanel
          requiredDocuments={step.required_documents}
          documents={candidate.documents}
          candidateId={candidate.id}
          candidateStepId={step.candidate_step_id}
          fields={fields}
          answers={answers}
        />

        {missingDocuments.length > 0 && (
          <p className="rounded-md bg-gray-100 px-3 py-2 text-sm text-gray-600">
            Required documents must be uploaded and verified before registration can be completed.
          </p>
        )}
      </div>
    </Modal>
  );
}
